import Vue from 'vue';
import Router from 'vue-router';
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta';
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress';
import rootStore from '@/state/store';
import routes from './routes';

// Attach router/meta plugins to vue
Vue.use(Router);
Vue.use(VueMeta, {
	// The component option name that vue-meta looks for meta info on.
	keyName: 'page',
});

// Async function to init router and return $auth for use in guard
const initAuth = async () => {
	let configErr;
	if (Vue.prototype.$auth === void 0) {
		try {
			await rootStore.dispatchGetConfig('/config.json');
		} catch (e) {
			configErr = true;
		}
	}
	if (configErr) {
		return false;
	}
	if (rootStore.state.config && !rootStore.state.userInfo) {
		// Ensure there isn't already an 'authenticating' message before adding another one
		rootStore.commitRemoveMessage('authenticating');
		rootStore.commitAddMessage({
			id: 'authenticating',
			type: 'info',
			message: 'Authenticating...',
			timeout: 0,
		});
	}
	return Vue.prototype.$auth;
};

// Create router
const router = new Router({
	routes,
	// Use the HTML5 history API (i.e. normal-looking routes)
	// instead of routes with hashes (e.g. example.com/#/about).
	// This may require some server configuration in production:
	// https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
	mode: 'history',
	// Simulate native-like scroll behavior when navigating to a new
	// route and using back/forward buttons.
	scrollBehavior({ }, { }, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		}
		return { x: 0, y: 0 };
	},
});

// Before each route evaluates...
router.beforeEach(async (to, from, next) => {
	// Skip auth check on auth-error, avoid endless loop
	if (to.name === 'auth-error') {
		return next();
	}

	// Skip auth check on 404, avoid endless loop
	if (to.name === '404') {
		return next();
	}

	// Ensure vue.prototype.$auth is available for templates
	const vueAuth = await initAuth();

	// Auth fails init when config is missing
	if (!vueAuth) {
		return next({
			name: '404',
			params: {
				code: 'auth_guard',
				message: 'Unable to load app configuration',
			},
		});
	}

	// Apply auth redirect guard
	try {
		const guard = await vueAuth.authRedirectGuard();
		return guard(to, from, next);
	} catch (e) {
		// Redirect to auth-error when not auth'd
		return next({
			name: 'auth-error',
			params: {
				code: 'auth_redirect_guard',
			},
		});
	}
});

// Verify granular access to routes
router.beforeEach(async (to, { }, next) => {
	// If page doesn't require any specific roles, skip this guard
	if (!to.meta.requiresRole) {
		return next();
	}

	// Retrieve userinfo and check against page meta info
	let userInfo = rootStore.state.userInfo;

	// If userinfo doesn't yet exist in store, get from okta directly (if available)
	if (!userInfo) {
		const app = router.app;
		const oktaUserInfo: IUser = await app.$auth.getUser();
		const oktaAccessToken = await app.$auth.getAccessToken();
		rootStore.commitSetUser({
			user: oktaUserInfo,
			accessToken: oktaAccessToken,
		});
		userInfo = rootStore.state.userInfo;
	}

	// Verify user meets requirements
	const groups = rootStore.getUserRoles;
	if (userInfo && groups.indexOf(to.meta.requiresRole) !== -1) {
		return next();
	}

	// Redirect to error
	return next({
		name: 'auth-error',
		params: {
			code: 'invalid_role',
			message: 'User does not have a role required to view this resource',
		},
	});
});

// After navigation is confirmed, but before resolving...
router.beforeResolve(({ }, routeFrom, next) => {
	// If this isn't an initial page load...
	if (routeFrom.name !== null) {
		// Start the route progress bar.
		NProgress.start();
	}
	next();
});

// When each route is finished evaluating...
router.afterEach(() => {
	// Complete the animation of the route progress bar.
	NProgress.done();
});

export default router;
